import { saleLineKindSchema } from "@station/pricing-engine";
import { z } from "zod";
import { businessUnitTypeSchema } from "../auth/features.js";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, moneySchema, quantitySchema, timestampSchema, uuidSchema, } from "../common/primitives.js";
import { subscriptionBillingSchema, subscriptionStatusSchema } from "./enums.js";
/**
 * Abonelik (ADR 0017 §27): plan + müşteri aboneliği + dönem ücreti + hak kullanımı. Etkinleştirme (a) tanınmış müşteri
 * kasada plan ürününü alınca worker'da, (b) filo carisi için web'den (dönem ücreti STANDART satış faturası — faturalama
 * turu), (c) gerekçeli ücretsiz tanım (kasa aboneliği, ücret kaydı yok). Aşım REDDEDİLMEZ, işaretlenir.
 */
const versionSchema = z.number().int().min(1).describe("İyimser kilit sürümü");
const positiveQuantitySchema = quantitySchema.refine((value) => Number(value) > 0, "Miktar sıfırdan büyük olmalıdır");
const positiveMoneySchema = moneySchema.refine((value) => BigInt(value) > 0n, "Tutar sıfırdan büyük olmalıdır");
export const subscriptionPlanSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    code: z.string(),
    name: z.string(),
    businessUnit: businessUnitTypeSchema,
    coveredProductIds: z.array(uuidSchema),
    coveredLineKinds: z.array(saleLineKindSchema),
    quantityPerPeriod: z.string(),
    dailyLimit: z.string().nullable(),
    period: z.literal("MONTH"),
    vehicleBound: z.boolean(),
    price: moneySchema.describe("Dönem ücreti, KDV dâhil (kuruş)"),
    productId: uuidSchema
        .nullable()
        .describe("Kasada satılan plan ürünü (tanınmış müşteri alınca abonelik açılır)"),
    isActive: z.boolean(),
    activeSubscriptions: z.number().int(),
    version: z.number().int(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
export const subscriptionPlanPageSchema = pageOf(subscriptionPlanSchema);
export const listSubscriptionPlansQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    isActive: z
        .enum(["true", "false"])
        .transform((value) => value === "true")
        .optional(),
});
const coverageRule = (value, context) => {
    if (value.coveredProductIds !== undefined &&
        value.coveredLineKinds !== undefined &&
        value.coveredProductIds.length + value.coveredLineKinds.length === 0)
        context.addIssue({
            code: "custom",
            path: ["coveredProductIds"],
            message: "Plan en az bir ürünü ya da satır türünü kapsamalıdır",
        });
};
export const createSubscriptionPlanSchema = z
    .object({
    companyId: uuidSchema,
    code: z
        .string()
        .trim()
        .regex(/^[A-Z0-9][A-Z0-9_-]{1,39}$/, "Kod büyük harf, rakam, _ ve - (2–40 karakter)"),
    name: z.string().trim().min(2).max(120),
    businessUnit: businessUnitTypeSchema,
    coveredProductIds: z.array(uuidSchema).max(200).default([]),
    coveredLineKinds: z.array(saleLineKindSchema).max(5).default([]),
    quantityPerPeriod: positiveQuantitySchema,
    dailyLimit: positiveQuantitySchema.nullable().default(null),
    vehicleBound: z.boolean().default(false),
    price: positiveMoneySchema,
    productId: uuidSchema.nullable().default(null),
})
    .superRefine(coverageRule);
export const updateSubscriptionPlanSchema = z
    .object({
    name: z.string().trim().min(2).max(120).optional(),
    coveredProductIds: z.array(uuidSchema).max(200).optional(),
    coveredLineKinds: z.array(saleLineKindSchema).max(5).optional(),
    quantityPerPeriod: positiveQuantitySchema.optional(),
    dailyLimit: positiveQuantitySchema.nullable().optional(),
    vehicleBound: z.boolean().optional(),
    price: positiveMoneySchema.optional(),
    productId: uuidSchema.nullable().optional(),
    isActive: z.boolean().optional(),
    version: versionSchema,
})
    .superRefine(coverageRule);
export const subscriptionUsageSummarySchema = z.object({
    today: localDateSchema,
    periodStart: localDateSchema.nullable().describe("Bugünü kapsayan dönem; yoksa null (dönem bitti)"),
    periodEnd: localDateSchema.nullable(),
    quantityPerPeriod: z.string(),
    used: z.string(),
    remaining: z.string().describe("Negatif olmaz; aşım `used`'da görünür"),
    dailyLimit: z.string().nullable(),
    usedToday: z.string(),
});
export const customerSubscriptionSchema = z.object({
    id: uuidSchema,
    planId: uuidSchema,
    planCode: z.string(),
    planName: z.string(),
    companyId: uuidSchema,
    customerProfileId: uuidSchema.nullable(),
    customerCode: z.string().nullable().describe("Abone müşteri profilinin kodu (filo carisi abonesinde null)"),
    customerMaskedName: z
        .string()
        .nullable()
        .describe("Abone müşterinin MASKELİ adı (`A*** Y***`; adsız / anonim profilde `—`; filo abonesinde null)"),
    counterpartyId: uuidSchema.nullable(),
    counterpartyCode: z.string().nullable().describe("Abone filo carisinin kodu (müşteri abonesinde null)"),
    counterpartyName: z
        .string()
        .nullable()
        .describe("Abone filo carisinin unvanı; bireysel (şahıs) cari adı `customers.personal.view` yoksa maskeli (müşteri abonesinde null)"),
    vehicleId: uuidSchema.nullable(),
    status: subscriptionStatusSchema,
    billing: subscriptionBillingSchema,
    currentPeriodStart: localDateSchema,
    currentPeriodEnd: localDateSchema,
    autoRenew: z.boolean(),
    startedAt: timestampSchema,
    activatedBySaleId: uuidSchema.nullable(),
    cancelledAt: timestampSchema.nullable(),
    cancelReason: z.string().nullable(),
    usage: subscriptionUsageSummarySchema,
    version: z.number().int(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
export const customerSubscriptionPageSchema = pageOf(customerSubscriptionSchema);
export const subscriptionChargeSchema = z.object({
    id: uuidSchema,
    periodStart: localDateSchema,
    periodEnd: localDateSchema,
    source: z.enum(["POS_SALE", "INVOICE"]),
    sourceId: uuidSchema,
    sourceLineRef: z.string(),
    amount: moneySchema,
    createdAt: timestampSchema,
});
export const customerSubscriptionDetailSchema = customerSubscriptionSchema.extend({
    charges: z.array(subscriptionChargeSchema),
});
export const listCustomerSubscriptionsQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema.optional(),
    customerProfileId: uuidSchema.optional(),
    counterpartyId: uuidSchema.optional(),
    planId: uuidSchema.optional(),
    status: subscriptionStatusSchema.optional(),
});
export const createCustomerSubscriptionSchema = z
    .object({
    planId: uuidSchema,
    customerProfileId: uuidSchema.nullable().default(null),
    counterpartyId: uuidSchema.nullable().default(null),
    vehicleId: uuidSchema.nullable().default(null),
    billing: subscriptionBillingSchema,
    startDate: localDateSchema.nullable().default(null).describe("Boşsa bugün (İstanbul)"),
    autoRenew: z.boolean().default(true),
    complimentaryReason: z
        .string()
        .trim()
        .min(3)
        .max(300)
        .nullable()
        .default(null)
        .describe("Kasa (POS) faturalı abonelik web'den YALNIZCA gerekçeli ücretsiz tanım olarak açılır"),
})
    .superRefine((value, context) => {
    const issue = (path, message) => context.addIssue({ code: "custom", path: [path], message });
    if ((value.customerProfileId === null) === (value.counterpartyId === null))
        issue("customerProfileId", "Abone müşteri profili YA DA filo carisi olmalıdır (tam olarak biri)");
    if (value.counterpartyId !== null && value.billing !== "INVOICE")
        issue("billing", "Filo carisinin aboneliği faturayla ücretlendirilir");
    if (value.billing === "POS" && value.complimentaryReason === null)
        issue("complimentaryReason", "Kasa aboneliği plan ürününün kasada satın alınmasıyla açılır; web'den yalnızca gerekçeli ücretsiz tanım yapılır");
    if (value.billing === "INVOICE" && value.complimentaryReason !== null)
        issue("complimentaryReason", "Faturalı abonelik ücretsiz tanımlanamaz");
});
export const subscriptionActionSchema = z.object({
    version: versionSchema,
    reason: z.string().trim().min(3).max(300).nullable().default(null),
});
export const subscriptionUsageSchema = z.object({
    id: uuidSchema,
    periodStart: localDateSchema,
    quantity: z.string().describe("İade karşı kaydında negatif"),
    occurredAt: timestampSchema,
    localDay: localDateSchema,
    stationId: uuidSchema,
    /** Kasa satışı; müşteri uygulamasından yıkama başlatmada (Prompt 18) kasa satışı YOKTUR → null. */
    posSaleId: uuidSchema.nullable(),
    /** Kullanımı yazan modül: `POS` (kasa satış satırı) ya da `WASH` (uygulamadan başlatma, `sourceDocId` = aktivasyon). */
    sourceModule: z.enum(["POS", "WASH"]),
    sourceDocId: uuidSchema.nullable(),
    sourceLineRef: z.string(),
    overuse: z.boolean(),
    createdAt: timestampSchema,
});
export const subscriptionUsagePageSchema = pageOf(subscriptionUsageSchema);
export const createSubscriptionBillingRunSchema = z.object({
    companyId: uuidSchema,
    asOf: localDateSchema.nullable().default(null).describe("Fatura tarihi ve vade günü; boşsa bugün"),
});
export const subscriptionBillingRunResultSchema = z.object({
    asOf: localDateSchema,
    invoices: z.array(z.object({
        invoiceId: uuidSchema,
        counterpartyId: uuidSchema,
        subscriptionIds: z.array(uuidSchema),
        amount: moneySchema,
    })),
    skipped: z.array(z.object({ subscriptionId: uuidSchema, reason: z.string() })),
});
//# sourceMappingURL=subscriptions.js.map